import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw1115f1Api, F115 } from '../../../services/pw1115f1Api';

export type Mode = 'browse' | 'add' | 'edit';

export function usePackingForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F115[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [desc, setDesc] = useState('');
  const [isOld, setIsOld] = useState(false);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pw1115f1Api.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.f115_01);
      setDesc(current.f115_11);
      setIsOld(current.f115_12);
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setCode('');
    setDesc('');
    setIsOld(false);
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setCode(current.f115_01);
    setDesc(current.f115_11);
    setIsOld(current.f115_12);
  };

  // Copy current record (enter add mode with copied data)
  const startCopy = () => {
    if (!current) return;
    setMode('add');
    setCode('');
    setDesc(current.f115_11);
    setIsOld(current.f115_12);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setCode(current.f115_01);
      setDesc(current.f115_11);
      setIsOld(current.f115_12);
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await pw1115f1Api.create({ f115_01: code, f115_11: desc });
        message.success('新增成功');
      } else {
        await pw1115f1Api.update(current!.f115_01, { f115_11: desc, f115_12: isOld });
        message.success('修改成功');
      }
      const targetCode = mode === 'add' ? code : current!.f115_01;
      const data = await loadRecords();
      const idx = data.findIndex((r: F115) => r.f115_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await pw1115f1Api.delete(current.f115_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search (jump to nearest match)
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f115_01 >= searchText || r.f115_11.includes(searchText));
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode,
    desc, setDesc,
    isOld, setIsOld,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, startCopy, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
